import React, { useEffect, useRef } from 'react';
import { Button, LinkButton, TagChip, ErrorBox, ExternalLink, FitBars } from '../../ui/index.js';
import { IconLock, IconArrowLeft } from '../../ui/icons.js';
import { formatDate, money } from '../../lib/formatters.mjs';
import { RequestStatus } from '../../components/RequestStatus.jsx';
import { STAGE_LABELS } from '../../../shared/stages.mjs';
import { RequestContent } from '../requests/RequestPanels.jsx';
import { fitItems } from './fit.js';
import './event-page.css';

// Event detail page (Kaan, 5 Kasım 2026): Contra's job post layout. Every opportunity opens here, before and after an
// offer request. Left: identity, description, fit, organizer, then the offer process. Right: a sticky facts card with
// the one primary action, or the state of the request once it exists. Fields without data are not shown at all.
const SCALE = { s: '1.000 kişiye kadar', m: '1.000+ kişi', l: '5.000+ kişi', xl: '20.000+ kişi' };
const scaleOf = c => !Number.isFinite(c) || c <= 0 ? null : c >= 20000 ? 'xl' : c >= 5000 ? 'l' : c >= 1000 ? 'm' : 's';
const dateRange = row => row.startDate ? `${formatDate(row.startDate)}${row.endDate && row.endDate !== row.startDate ? ` – ${formatDate(row.endDate)}` : ''}` : '';
const hostOf = url => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; } };

function Facts({ row, opportunity }) {
  const place = [opportunity.venue, opportunity.city || row.location].filter(Boolean).join(', ');
  const scale = SCALE[scaleOf(Number(opportunity.capacity))];
  const items = [['Tarih', dateRange(row)], ['Yer', place], ['Ölçek', scale], ['Bedel', row.amount != null ? money(row.amount, row.currency) : 'Teklifle netleşir']].filter(([, v]) => v);
  return <dl className="bs-event-facts">{items.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

function TeamFacts({ row, opportunity }) {
  const c = row.criteria || {}, pct = v => v == null ? '—' : `%${Math.round(v * 100)}`;
  const items = [['Uyum puanı', row.score != null ? row.score.toFixed(2) : '—'], ['Sıra', row.rank != null ? `${row.rank + 1}.` : '—'],
    ['Markada', row.revealed === false ? 'Kilitli' : 'Açık'], ['Kitle / hedef', `${pct(c.audience)} / ${pct(c.goal)}`], ['Kaynak', opportunity.source || '—']];
  return <section className="bs-event-card bs-event-team" aria-labelledby="event-team">
    <h2 id="event-team">Ekip için</h2>
    <dl className="bs-event-facts">{items.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
    {opportunity.sourceUrl && <p className="bs-event-muted"><ExternalLink href={opportunity.sourceUrl}>Kaynak sayfayı aç</ExternalLink></p>}
  </section>;
}

export function EventPage({ row, campaign, team, canEdit, meeting, busy, error, onBack, backLabel, onRequest, onSave, onCampaignNavigate }) {
  const opportunity = row.opportunity || {}, locked = row.locked, request = row.request;
  const title = locked ? 'Adı kilitli etkinlik' : row.name;
  const tags = [opportunity.category || row.category, opportunity.subcategory, opportunity.city].filter(Boolean);
  const source = !locked && opportunity.sourceUrl ? hostOf(opportunity.sourceUrl) : '';
  const fit = row.criteria ? fitItems(row.criteria, { campaign, opportunity }) : null;
  const titleRef = useRef(null);
  // Opened from a list: move focus to the title; Escape goes back unless a menu or dialog handled it first.
  useEffect(() => { titleRef.current?.focus(); window.scrollTo?.(0, 0); }, [row.id]);
  useEffect(() => { const onKey = e => { if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('[role="dialog"],[role="menu"],[role="listbox"],[role="tooltip"]')) onBack(); }; window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onBack]);

  let action = null, note = null;
  if (request) {
    note = 'Sıradaki adım ve teklifler aşağıdaki Teklif süreci bölümünde.';
  } else if (locked) {
    action = meeting?.href && !meeting?.booked ? <Button variant="solid" color="accent" onClick={() => { if (meeting.external) window.open(meeting.href, '_blank', 'noopener'); else location.href = meeting.href; }}>Ekibimizle görüşün</Button> : null;
    note = meeting?.booked ? 'Görüşmeniz planlandı; bu etkinliği o görüşmede açarız.' : 'Adını ve teklifini ekibimizle görüşmede birlikte açarız.';
  } else if (!team) {
    action = <Button variant="solid" color="accent" disabled={!canEdit || row.needsReview || campaign?.archived} loading={busy} onClick={() => onRequest(row.id)}>Teklif iste</Button>;
    note = 'Teklif istemek satın alma ya da rezervasyon oluşturmaz.';
  }
  const byline = [opportunity.category || row.category, dateRange(row)].filter(Boolean).join(' · ');

  return <article className="bs-event-page" aria-labelledby="event-title">
    <nav className="bs-event-back" aria-label="Geri"><LinkButton onClick={onBack} aria-label={`${backLabel || campaign?.name || 'Kampanya'} sayfasına dön`}><IconArrowLeft size={16} aria-hidden="true" />{backLabel || campaign?.name || 'Kampanya'}</LinkButton></nav>
    <ErrorBox error={error} />
    <div className="bs-event-grid">
      <header className="bs-event-head">
        <div className="bs-event-status"><RequestStatus stage={request?.stage || 'RECOMMENDED'} label={request ? undefined : 'Önerildi'} />{locked && <TagChip><IconLock size={12} aria-hidden="true" /> Kilitli</TagChip>}</div>
        <h1 id="event-title" className="bs-event-title" tabIndex={-1} ref={titleRef}>{title}</h1>
        {(byline || (opportunity.organizer && !locked)) && <p className="bs-event-byline">{opportunity.organizer && !locked ? <><span className="bs-event-avatar" aria-hidden="true">{opportunity.organizer.trim()[0]}</span><strong>{opportunity.organizer}</strong></> : null}
          {byline && <span>{byline}</span>}</p>}
      </header>
      <div className="bs-event-main">
        {!locked && opportunity.description && <p className="bs-event-description">{opportunity.description}</p>}

        {fit && <section className="bs-event-section" aria-labelledby="event-fit">
          <h2 id="event-fit">Markanız için uygunluk</h2>
          <FitBars items={fit} label="Bu etkinliğin markanız için uygunluk ölçütleri" />
        </section>}

        {!locked && opportunity.organizer && <section className="bs-event-card" aria-labelledby="event-org">
          <h2 id="event-org">Düzenleyen hakkında</h2>
          <p><strong>{opportunity.organizer}</strong></p>
          {source && <p className="bs-event-muted">Kaynak: <ExternalLink href={opportunity.sourceUrl}>{source}</ExternalLink></p>}
        </section>}

        {request && <section className="bs-event-section bs-event-process" aria-labelledby="event-process">
          <h2 id="event-process">Teklif süreci</h2>
          <RequestContent request={request} campaign={campaign} opportunity={opportunity} team={team} onSave={onSave} onCampaignNavigate={onCampaignNavigate} />
        </section>}
      </div>
      <aside className="bs-event-aside" aria-label="Etkinlik bilgileri">
        <div className="bs-event-sticky">
          <div className="bs-event-card">
            <h2>{request ? 'Teklif talebi' : 'Bilgiler'}</h2>
            {request && <dl className="bs-event-facts"><div><dt>Durum</dt><dd>{STAGE_LABELS[request.stage] || request.stage}</dd></div><div><dt>Talep tarihi</dt><dd>{formatDate(request.createdAt)}</dd></div></dl>}
            <Facts row={row} opportunity={opportunity} />
            {action}
            {note && <p className="bs-event-muted">{note}</p>}
            {tags.length > 0 && <div className="bs-event-tags"><h3>Etiketler</h3><div>{tags.map(t => <TagChip key={t}>{t}</TagChip>)}</div></div>}
          </div>
          {team && <TeamFacts row={row} opportunity={opportunity} />}
        </div>
      </aside>
    </div>
  </article>;
}
